<template>
  <div class="login-wrapper">
    <div class="login-container">
      <!-- Logo -->
      <img
        :src="'/static/icons/BaanRueJai.svg'"
        class="logo"
        alt="BaanRueJai Logo"
      />

      <!-- Employee Section -->
      <section class="mb-5">
        <h2 class="section-title ms-title">สำหรับพนักงาน</h2>
        <sc-button variant="white" class="btn-block" @click="msLogin">
          <img
            src="/static/icons/microsoft-icon.svg"
            class="mr-2"
            alt="Microsoft Icon"
          />
          ลงชื่อเข้าใช้ด้วย Microsoft
        </sc-button>
      </section>

      <el-divider class="section-divider mb-5">หรือ</el-divider>

      <!-- Partner Section -->
      <section>
        <h2 class="section-title partner-title">สำหรับพาร์ทเนอร์</h2>

        <el-input
          v-model="email"
          name="email"
          v-validate="'required|email'"
          autocomplete="username"
          :placeholder="$t('feature.login.email')"
          clearable
          :class="{ error: errors.has('email') || error }"
          class="mb-3"
        ></el-input>

        <el-input
          v-model="password"
          name="password"
          v-validate="'required|min:6'"
          autocomplete="current-password"
          :placeholder="$t('feature.login.password')"
          clearable
          show-password
          :class="{ error: errors.has('password') || error }"
          class="mb-2"
        ></el-input>

        <div v-if="combinedError" class="alert-error">
          <i class="fa fa-info-circle"></i>
          {{ combinedError }}
        </div>

        <sc-button
          variant="white-orange"
          class="partner-login-btn"
          @click="onSubmit"
        >
          {{ $t("feature.login.label") }}
        </sc-button>
      </section>
    </div>

    <!-- Version info fixed bottom right -->
    <div class="version-info">
      <small>{{ $t("version") }} {{ version }}</small>
    </div>
  </div>
</template>

<script>
import ScButton from "@/lib/components/ScButton";
import { version } from "@/../package.json";
import { baanRueJaiClient } from "@/dependencies";
import { backendAdminClient } from "@/dependencies";
import { setCookie, Auth, unsetCookie } from "@/lib/cookie";

export default {
  components: {
    ScButton
  },
  data() {
    return {
      email: "",
      password: "",
      error: false,
      version,
      errorMessage: ""
    };
  },
  computed: {
    combinedError() {
      // If both email and password are empty, no error to show
      if (!this.email && !this.password) {
        return null;
      }

      if (this.errorMessage) {
        return this.errorMessage;
      }

      const emailError = this.errors.first("email");
      const passwordError = this.errors.first("password");

      if (emailError) return emailError;
      if (passwordError) return passwordError;

      return this.error ? this.$t("feature.login.invalid") : null;
    }
  },
  methods: {
    onSubmit() {
      this.$validator.validateAll().then(result => {
        if (result) {
          unsetCookie(Auth.AccessToken);
          unsetCookie(Auth.RefreshToken);

          if (this.isEmployeeEmail(this.email)) {
            this.errorMessage = this.$t("feature.login.login_sso_only");
            return;
          }

          this.$http
            .post(`${process.env.HOST}/api/admin/auth/login`,
              {
                email: this.email,
                password: this.password
              },
              {
                headers: { 'X-Frontend-Source': 'sc-frontend' }
              }
            )
            .then(
              response => {
                const accessToken = response.body.accessToken;
                baanRueJaiClient.setAuthorizationToken(accessToken);
                setCookie(Auth.AccessToken, accessToken);
                setCookie(Auth.RefreshToken, response.body.refreshToken);
                this.$http
                  .get(`${process.env.HOST}/api/admin/auth/`, {
                    headers: {
                      Authorization: `Bearer ${accessToken}`,
                      "Cache-Control": "no-cache"
                    }
                  })
                  .then(res => {
                    localStorage.setItem("adminFirstName", res.body.firstName);
                    localStorage.setItem("adminLastName", res.body.lastName);
                    localStorage.setItem("adminRole", res.body.role);
                    localStorage.setItem(
                      "adminRolePermission",
                      JSON.stringify(res.body.adminRole)
                    );
                    localStorage.setItem(
                      "adminRoleId",
                      res.body.adminRole.roleId
                    );
                    localStorage.setItem("adminId", res.body.adminId);
                    localStorage.setItem(
                      "adminProjects",
                      res.body.adminProjects
                        .map(project => project.projectId)
                        .join()
                    );
                    if (this.$route.query.redirect !== undefined) {
                      this.$router.push({ path: this.$route.query.redirect });
                    } else {
                      this.setDefaultRouteByPermission(res.body.adminRole);
                    }
                  });
              },
              response => {
                if (response.status !== 200) {
                  const errorCode = response.body && response.body.errorCode;

                  if (errorCode === "EMPLOYEE_USE_SSO_ONLY") {
                    this.errorMessage = this.$t("feature.login.login_sso_only");
                  } else {
                    this.errorMessage = this.$t("feature.login.invalid");
                  }
                }
              }
            )
            .catch(e => {
              this.errorMessage = e.response
                ? e.response.data.message || this.$t("feature.login.invalid")
                : this.$t("feature.login.error");
            });
        }
      });
    },
    setDefaultRouteByPermission(permission = null) {
      if (permission && permission.features) {
        const firstFeature = permission.features[0];
        switch (firstFeature.featureId) {
          case 1:
            this.$router.push({
              name: "ArticleList",
              params: { articleType: "news" }
            });
            break;
          case 2:
            this.$router.push({
              name: "ArticleList",
              params: { articleType: "activities" }
            });
            break;
          case 3:
            this.$router.push({
              name: "ArticleList",
              params: { articleType: "privileges" }
            });
            break;
          case 4:
            this.$router.push({ name: "ContactProject" });
            break;
          default:
            this.$router.push({ name: "Dashboard" });
        }
      } else {
        this.$router.push({ name: "Dashboard" });
      }
    },
    msLogin() {
      backendAdminClient.auth.microsoft.login();
    },
    isEmployeeEmail(email) {

      // Environment Vars
      const { NODE_ENV } = process.env

      // Validate Environment
      if (NODE_ENV == 'local') return false

      // Validate Email
      if (!email) return false;

      const lowerEmail = email.toLowerCase();

      return (
        lowerEmail.endsWith("@scasset.com") ||
        lowerEmail.endsWith("@scasble.com")
      );
    },
    clearErrorMessage() {
      if (this.errorMessage) {
        this.errorMessage = "";
      }
    },
  },
  watch: {
    email() {
      this.clearErrorMessage();
    },
    password() {
      this.clearErrorMessage();
    }
  },
};
</script>

<style lang="scss" scoped>
.login-wrapper {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 24px;
  background-color: white;
  overflow: hidden;
}

.login-container {
  width: 100%;
  max-width: 320px;
  border-radius: 8px;
  z-index: 1;
  box-sizing: border-box;
  color: #333;
}

.logo {
  margin: 0 auto 48px;
  max-width: 180px;
}

.section-title {
  font-weight: 600;
  margin-bottom: 16px;
  text-align: left;
}
.ms-title {
  font-size: 1.3rem;
}
.partner-title {
  font-size: 1rem;
}

.section-divider {
  border-color: rgba(95, 95, 95, 0.8);
}

.alert-error {
  color: red;
  font-size: 13px;
  text-align: left;
  margin-bottom: 8px;
}

.partner-login-btn {
  display: inline-block;
  padding: 8px 24px;
  border: 1.5px solid #ff7a00 !important;
  color: #ff7a00 !important;
  background: transparent !important;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  margin-top: 12px;
  border-radius: 4px;
}
.partner-login-btn:hover,
.partner-login-btn:focus {
  background-color: rgba(255, 122, 0, 0.15) !important;
  color: #ff7a00 !important;
  border-color: #ff7a00 !important;
}

.version-info {
  position: fixed;
  bottom: 16px;
  right: 16px;
  color: #333;
  font-size: 0.9rem;
}

.el-input {
  width: 100%;
}

@media (max-width: 480px) {
  .login-container {
    max-width: 100%;
    padding: 16px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/admin/Login.vue